// Ctrl (Cmd on a Mac) and click on another product puts the selected one
// beside it (CO-5857): flush against the side clicked, its nearer end lined
// up with the other's, at its own height. A click on the top, which names no
// side, uses the side the selected product is already nearer.
import * as THREE from 'three';
import {axisIndex} from 'components/customer/RoomPlanner/helpers/axes';
import type {PlanAxis} from 'components/customer/RoomPlanner/helpers/calculations';

// A face whose normal leans further than this from level (about 45 degrees)
// is a top or bottom, not a side.
const SIDE_NORMAL = 0.7;

/**
 * A side of a product: across which floor axis, and which way it looks.
 */
export interface ProductSide {
    axis: PlanAxis;
    side: -1 | 1;
}

/**
 * @param {THREE.Box3} selected The selected product's box.
 * @param {THREE.Box3} target The other product's box.
 * @param {PlanAxis} axis The floor axis the side is across.
 * @param {number} side Which way the side looks along it.
 * @return {THREE.Vector3} What to add to the selected product's position to
 * put it flush against that side with its nearer end lined up, at its own
 * height.
 */
export const placeBeside = (
    selected: THREE.Box3,
    target: THREE.Box3,
    axis: PlanAxis,
    side: -1 | 1
): THREE.Vector3 => {
    const index = axisIndex(axis);
    const across = axisIndex(axis === 'x' ? 'z' : 'x');
    const offset = new THREE.Vector3();
    offset.setComponent(
        index,
        side > 0
            ? target.max.getComponent(index) - selected.min.getComponent(index)
            : target.min.getComponent(index) - selected.max.getComponent(index)
    );
    const toMin =
        target.min.getComponent(across) - selected.min.getComponent(across);
    const toMax =
        target.max.getComponent(across) - selected.max.getComponent(across);
    offset.setComponent(
        across,
        Math.abs(toMin) <= Math.abs(toMax) ? toMin : toMax
    );

    return offset;
};

/**
 * @param {THREE.Vector3 | null} normal The world normal of the face clicked,
 * if known.
 * @param {THREE.Box3} selected The selected product's box.
 * @param {THREE.Box3} target The other product's box.
 * @return {ProductSide} The side clicked, or, for a top, bottom or no face,
 * the side the selected product is already nearer.
 */
export const sideClicked = (
    normal: THREE.Vector3 | null,
    selected: THREE.Box3,
    target: THREE.Box3
): ProductSide => {
    const way =
        normal && Math.abs(normal.y) < SIDE_NORMAL
            ? normal
            : selected
                  .getCenter(new THREE.Vector3())
                  .sub(target.getCenter(new THREE.Vector3()));
    const axis: PlanAxis = Math.abs(way.x) >= Math.abs(way.z) ? 'x' : 'z';

    return {axis, side: way.getComponent(axisIndex(axis)) >= 0 ? 1 : -1};
};
